import { useState } from 'react';
import { Drawer, Box, Grid, Pagination } from 'components/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client/react';
import { FilterProvider, useFilter, initUserFilterValues } from 'contexts/filter';
import { useError } from 'contexts/error';
import { usePaginate } from 'contexts/paginate';
import Index from 'components/space_users/index';
import FilterForm from 'components/space_users/filter_form';
import FilterTags from 'components/filter/tags';
import FlexibleInput from 'components/flexible_input';
import CreateButton from 'components/space_users/button/create';
import PermissionsQuery from 'queries/space/permissions';
import FilterButton from 'components/space_users/button/filter';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';

const IndexContainer = () => {
  const intl = useIntl();
  const { page, onPageChange } = usePaginate();
  const setError = useError();
  const { usersFilter, setUsersFilter } = useFilter();
  const [filterVisible, setFilterVisible] = useState(false);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState('email_asc');
  const {
    data = {
      space: { adminRole: true },
      spacePermissions: { permissions: [], perPage: 10, permissionsTotal: 0 },
    },
    loading,
    refetch,
  } = useQuery(PermissionsQuery, {
    variables: { ...usersFilter, search, page, sortBy },
    onError: () => {
      setError('error.users.index');
    },
  });

  const onRemove = () => {
    if (page > 1) {
      onPageChange(page - 1);
    } else {
      refetch();
    }
  };

  return (
    <FilterProvider>
      <TitleTypography variant="h1">
        <FormattedMessage id="navbar.users" />
      </TitleTypography>
      <SubtitleTypography variant="h2">
        <FormattedMessage
          id="user.total"
          values={{ count: data.spacePermissions.permissionsTotal ?? 0 }}
        />
      </SubtitleTypography>
      {filterVisible && (
        <Drawer
          anchor="right"
          open={filterVisible}
          onClose={() => {
            setFilterVisible(false);
          }}
        >
          <FilterForm
            initialValues={usersFilter}
            defaultValues={initUserFilterValues}
            onSubmit={(values) => {
              onPageChange(1);
              setUsersFilter(values);
            }}
            onClose={() => {
              setFilterVisible(false);
            }}
          />
        </Drawer>
      )}
      <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ flexGrow: { sm: 1 }, minWidth: 0 }}>
          <FlexibleInput
            placeholder={intl.formatMessage({ id: 'flexible_filter.user' })}
            value={search}
            onChange={setSearch}
          />
        </Grid>
        <Grid size="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <FilterButton
            onClick={() => setFilterVisible(!filterVisible)}
            initUserFilterValues={initUserFilterValues}
            usersFilter={usersFilter}
          />
          {data.space.adminRole && <CreateButton onSuccess={refetch} />}
        </Grid>
      </Grid>
      <FilterTags
        filterValues={usersFilter}
        defaultValues={initUserFilterValues}
        onFilterChange={setUsersFilter}
      />
      <Index
        loading={loading}
        permissions={data.spacePermissions.permissions}
        onRemove={onRemove}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <Box display="flex" justifyContent="center">
        {data.spacePermissions.permissionsTotalPages > 1 && (
          <Pagination
            count={data.spacePermissions.permissionsTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
    </FilterProvider>
  );
};

export default IndexContainer;
